import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { buyerPosts } from "@/mocks/products";
import PostRequirementPanel from "./components/PostRequirementPanel";
import BuyerAuthModal from "@/pages/home/components/BuyerAuthModal";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

const categories = ["All", "Electronics", "Apparel", "Machinery", "Auto Parts", "Health & Beauty", "Food & Beverage", "Chemicals", "Building Materials", "Safety Equipment", "Sports & Fitness", "Toys", "Vehicles", "Energy", "Home & Garden"];

type View = "browse" | "post";

export default function BuyerPostPage() {
  useSEO({
    title: "Buyer Requirements & Quote Board | Post Sourcing Requests | Crown Global",
    description: "Post your product sourcing requirements for free on Crown Global. Verified factories with membership can view and send competitive quotes. 28 active buyer requests from 22+ countries.",
    keywords: "buyer requirements, sourcing requests, quote board, factory quotes, bulk sourcing, RFQ platform",
    canonical: "/buyer-post",
    ogTitle: "Buyer Requirements & Quote Board — Crown Global",
    ogDescription: "Post sourcing requirements free. Verified factories send competitive quotes. 28+ active requests from global buyers.",
    ogImage: "https://public.readdy.ai/ai/img_res/45d7a5ebfade3e2c8dd4c7e5d4ab93ce.jpg",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/buyer-post`,
          "url": `${SITE_URL}/buyer-post`,
          "name": "Buyer Requirements & Quote Board — Crown Global",
          "description": "Post product sourcing requirements for free. Verified factories with membership can view and send competitive quotes.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Buyer Post", "item": `${SITE_URL}/buyer-post` }
            ]
          }
        },
        {
          "@type": "Service",
          "@id": `${SITE_URL}/buyer-post#service`,
          "url": `${SITE_URL}/buyer-post`,
          "name": "Crown Global Buyer RFQ & Quote Board",
          "description": "Free platform for buyers to post sourcing requirements and receive competitive quotes from verified global factories and suppliers.",
          "provider": { "@id": `${SITE_URL}/#organization` },
          "areaServed": "Worldwide",
          "serviceType": ["RFQ Platform", "Buyer Sourcing", "Factory Quote Board", "Bulk Procurement"],
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free for buyers to post sourcing requirements"
          }
        }
      ]
    }
  });

  const navigate = useNavigate();
  const [view, setView] = useState<View>("browse");
  const [activeCategory, setActiveCategory] = useState("All");
  const [showMembershipGate, setShowMembershipGate] = useState(false);
  const [lockedPostTitle, setLockedPostTitle] = useState("");
  const [postSuccess, setPostSuccess] = useState(false);
  const [showBuyerAuth, setShowBuyerAuth] = useState(false);
  const [loggedInBuyer, setLoggedInBuyer] = useState<string | null>(null);

  const handleSendQuote = (title: string) => {
    setLockedPostTitle(title);
    setShowMembershipGate(true);
  };

  const handlePostSuccess = () => {
    setView("browse");
    setPostSuccess(true);
    setTimeout(() => setPostSuccess(false), 5000);
  };

  const filtered = activeCategory === "All" ? buyerPosts : buyerPosts.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {showBuyerAuth && (
        <BuyerAuthModal
          onClose={() => setShowBuyerAuth(false)}
          onSuccess={(name) => { setLoggedInBuyer(name); setShowBuyerAuth(false); setView("post"); }}
        />
      )}

      {/* Hero banner */}
      <section className="relative pt-28 pb-10 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=global%20business%20buyers%20sourcing%20procurement%20professional%20meeting%20office%20international%20trade%20deal%20handshake%20modern%20corporate%20clean%20background&width=1600&height=500&seq=buyer_hero_bg_01&orientation=landscape"
            alt="Buyer Requirements"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/90 via-stone-900/70 to-stone-900/40"></div>
        </div>
        <div className="relative z-10 max-w-screen-2xl mx-auto px-4 md:px-8 py-8">
          <div className="max-w-2xl">
            <span className="inline-block bg-amber-400 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-3">Live Buyer Requests</span>
            <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-3">
              Buyer Requirements &amp; Quote Board
            </h1>
            <p className="text-stone-200 text-sm leading-relaxed mb-5">
              Real sourcing requests from verified global buyers. Post your requirements for <strong className="text-amber-400">free</strong> — factories with a membership can view and send competitive quotes.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex items-center gap-2 bg-green-500/20 border border-green-400/30 text-green-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                <i className="ri-checkbox-circle-fill"></i> Buyer Post — Always FREE
              </div>
              <div className="flex items-center gap-2 bg-amber-400/20 border border-amber-400/30 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                <i className="ri-vip-crown-line"></i> Factory View — Membership Required
              </div>
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                <i className="ri-file-list-3-line"></i> {buyerPosts.length} Active Requests
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => loggedInBuyer ? setView("post") : setShowBuyerAuth(true)}
                className={`flex items-center gap-2 font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap ${view === "post" ? "bg-white text-amber-600" : "bg-amber-400 hover:bg-amber-500 text-white"}`}
              >
                <i className="ri-add-line"></i>
                {loggedInBuyer ? "Post My Requirement" : "Post Requirement — Free"}
              </button>
              <button
                onClick={() => setView("browse")}
                className={`flex items-center gap-2 font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap ${view === "browse" ? "bg-white text-stone-800" : "bg-white/10 hover:bg-white/20 border border-white/30 text-white"}`}
              >
                <i className="ri-list-check-2"></i> Browse Requests
              </button>
              <button
                onClick={() => navigate("/membership")}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-vip-crown-line"></i> Factory Membership
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <div className="bg-amber-400">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: "ri-file-list-3-line", value: "28", label: "Active Buyer Requests" },
            { icon: "ri-global-line", value: "22+", label: "Countries Represented" },
            { icon: "ri-money-dollar-circle-line", value: "$2.8M+", label: "Total Budget Posted" },
            { icon: "ri-time-line", value: "< 4 hrs", label: "Avg. First Response" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
                <i className={`${s.icon} text-white text-base`}></i>
              </div>
              <div>
                <div className="text-white font-extrabold text-lg leading-none">{s.value}</div>
                <div className="text-amber-100 text-xs mt-0.5">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-10">

        {/* Success toast */}
        {postSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-5 py-4 mb-6 flex items-center gap-3">
            <i className="ri-checkbox-circle-fill text-xl"></i>
            <div>
              <p className="font-bold text-sm">Your requirement has been posted successfully!</p>
              <p className="text-xs mt-0.5">Verified factories will review and send you quotes within 4 hours.</p>
            </div>
          </div>
        )}

        {/* Logged-in buyer welcome bar */}
        {loggedInBuyer && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3 mb-6 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-full shrink-0">
              <i className="ri-user-smile-line text-emerald-600"></i>
            </div>
            <div className="flex-1">
              <p className="text-emerald-700 font-bold text-sm">Welcome, {loggedInBuyer}!</p>
              <p className="text-emerald-600 text-xs">You can post unlimited requirements for free.</p>
            </div>
            <button
              onClick={() => setView("post")}
              className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i>Post New Requirement
            </button>
          </div>
        )}

        {/* ── POST VIEW ── */}
        {view === "post" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <PostRequirementPanel
                isLoggedIn={!!loggedInBuyer}
                buyerName={loggedInBuyer || ""}
                onSuccess={handlePostSuccess}
                onLoginNeeded={() => setShowBuyerAuth(true)}
              />
            </div>
            {/* Sidebar tips */}
            <div className="flex flex-col gap-5">
              <div className="bg-white rounded-2xl p-5 border border-stone-100">
                <h3 className="font-extrabold text-stone-800 mb-4 flex items-center gap-2 text-sm">
                  <i className="ri-lightbulb-line text-amber-400"></i> Tips for Better Quotes
                </h3>
                <div className="flex flex-col gap-3">
                  {[
                    { icon: "ri-file-text-line", tip: "Be specific about product specs, materials, and dimensions" },
                    { icon: "ri-image-line", tip: "Mention if you have reference images or samples" },
                    { icon: "ri-money-dollar-circle-line", tip: "Include your target price range to attract the right factories" },
                    { icon: "ri-time-line", tip: "Set a realistic deadline — factories need time to prepare quotes" },
                    { icon: "ri-shield-check-line", tip: "Mention required certifications (CE, RoHS, FDA, etc.)" },
                    { icon: "ri-whatsapp-line", tip: "Add your WhatsApp for faster communication with factories" },
                  ].map((t, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                        <i className={`${t.icon} text-amber-500 text-sm`}></i>
                      </div>
                      <p className="text-xs text-stone-600 leading-relaxed">{t.tip}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                <h3 className="font-extrabold text-stone-800 mb-3 text-sm">How It Works</h3>
                <div className="flex flex-col gap-3">
                  {[
                    { step: "1", label: "Post Free", desc: "Submit your sourcing requirement — no cost, no registration fee" },
                    { step: "2", label: "Get Quotes", desc: "Member factories review and send competitive quotes within hours" },
                    { step: "3", label: "Choose & Connect", desc: "Compare quotes, exchange WhatsApp/email, close the deal" },
                  ].map(s => (
                    <div key={s.step} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-amber-400 text-white text-xs font-extrabold rounded-full shrink-0">{s.step}</div>
                      <div>
                        <p className="text-xs font-bold text-stone-800">{s.label}</p>
                        <p className="text-xs text-stone-500 mt-0.5">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-stone-900 rounded-2xl p-5 text-center">
                <div className="text-2xl mb-2">🏭</div>
                <p className="text-white font-bold text-sm mb-1">Are You a Factory?</p>
                <p className="text-stone-400 text-xs mb-4">Get membership to view buyer contact details and send quotes</p>
                <button
                  onClick={() => navigate("/membership")}
                  className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-2.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-vip-crown-line mr-1"></i>Get Membership
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── BROWSE VIEW ── */}
        {view === "browse" && (
          <>
            {/* How it works */}
            <div className="bg-white rounded-2xl p-6 mb-8 border border-stone-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: "ri-user-add-line", color: "bg-green-100 text-green-600", title: "Buyers Post for Free", badge: "FREE", badgeColor: "bg-green-100 text-green-600", desc: "Any buyer worldwide can post their sourcing requirements at no cost. No registration fee, no hidden charges." },
                  { icon: "ri-vip-crown-line", color: "bg-amber-100 text-amber-500", title: "Factories Need Membership", badge: "PAID", badgeColor: "bg-amber-100 text-amber-600", desc: "Factories and suppliers must hold an active membership to view full buyer details and send quotes." },
                  { icon: "ri-chat-3-line", color: "bg-stone-100 text-stone-600", title: "Connect & Close Deals", badge: "FAST", badgeColor: "bg-stone-100 text-stone-600", desc: "Buyers receive multiple competitive quotes and choose the best supplier. Average deal closes within 72 hours." },
                ].map(item => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${item.color}`}>
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-extrabold text-stone-900 text-sm">{item.title}</h4>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.badgeColor}`}>{item.badge}</span>
                      </div>
                      <p className="text-stone-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category filter + header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-extrabold text-stone-900">
                  {activeCategory === "All" ? `All ${buyerPosts.length} Buyer Requests` : `${filtered.length} Requests in ${activeCategory}`}
                </h2>
                <p className="text-stone-500 text-xs mt-0.5">Updated in real-time · Membership required to send quotes</p>
              </div>
              <button
                onClick={() => loggedInBuyer ? setView("post") : setShowBuyerAuth(true)}
                className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-5 py-2.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> Post Your Requirement — Free
              </button>
            </div>

            {/* Category tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-6">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === cat ? "bg-amber-400 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-600"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Posts grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filtered.map((post) => (
                <div key={post.id} className="bg-white rounded-2xl p-5 border border-stone-100 hover:border-amber-200 transition-colors group">
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="text-xs bg-amber-50 text-amber-600 font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap">{post.category}</span>
                        {post.verified && (
                          <span className="flex items-center gap-1 text-xs text-green-600 font-semibold bg-green-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                            <i className="ri-shield-check-fill"></i> Verified
                          </span>
                        )}
                        {post.urgent && (
                          <span className="flex items-center gap-1 text-xs text-red-600 font-semibold bg-red-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                            <i className="ri-alarm-line"></i> Urgent
                          </span>
                        )}
                      </div>
                      <h3 className="font-bold text-stone-800 text-sm leading-snug">{post.title}</h3>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs text-stone-400">{post.posted}</div>
                      <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-1 justify-end">
                        <i className="ri-chat-3-line text-amber-400"></i>
                        <span>{post.responses} quotes</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="bg-stone-50 rounded-lg px-3 py-2">
                      <div className="text-xs text-stone-400 mb-0.5">Budget</div>
                      <div className="text-xs font-bold text-stone-800">{post.budget}</div>
                    </div>
                    <div className="bg-stone-50 rounded-lg px-3 py-2">
                      <div className="text-xs text-stone-400 mb-0.5">Quantity</div>
                      <div className="text-xs font-bold text-stone-800">{post.quantity}</div>
                    </div>
                    <div className="bg-stone-50 rounded-lg px-3 py-2">
                      <div className="text-xs text-stone-400 mb-0.5">Deadline</div>
                      <div className="text-xs font-bold text-stone-800">{post.deadline}</div>
                    </div>
                    <div className="bg-stone-50 rounded-lg px-3 py-2">
                      <div className="text-xs text-stone-400 mb-0.5">Buyer</div>
                      <div className="text-xs font-bold text-stone-800 flex items-center gap-1">
                        <span>{post.flag}</span>
                        <span className="truncate">{post.buyer}</span>
                      </div>
                    </div>
                  </div>

                  {/* Blurred contact info */}
                  <div className="relative mb-4 rounded-xl overflow-hidden">
                    <div className="bg-stone-50 px-3 py-2.5 blur-sm select-none pointer-events-none">
                      <div className="text-xs text-stone-400 mb-1">Contact &amp; Full Details</div>
                      <div className="text-xs text-stone-700">📧 buyer@company.com · 📞 +1 (555) 000-0000 · Full spec sheet attached</div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-amber-600">
                        <i className="ri-lock-line"></i>
                        <span>Membership required to view</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                    <div className="flex items-center gap-1.5 text-xs text-stone-400">
                      <i className="ri-eye-line"></i>
                      <span>Members only</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate("/membership")}
                        className="text-xs text-amber-600 font-semibold hover:text-amber-700 cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-vip-crown-line"></i> Get Membership
                      </button>
                      <button
                        onClick={() => handleSendQuote(post.title)}
                        className="bg-amber-400 hover:bg-amber-500 text-white text-xs font-bold px-4 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Send Quote
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-10 bg-stone-900 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-white font-extrabold text-xl mb-1">Are You a Factory or Supplier?</h3>
                <p className="text-stone-400 text-sm">Get a membership to unlock all {buyerPosts.length} buyer requests, view full contact details, and send unlimited quotes.</p>
                <div className="flex flex-wrap gap-3 mt-3">
                  {["View buyer contact info", "Send unlimited quotes", "Priority listing in search", "Verified supplier badge"].map((f) => (
                    <div key={f} className="flex items-center gap-1.5 text-xs text-stone-300">
                      <i className="ri-checkbox-circle-fill text-amber-400"></i>
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => navigate("/membership")}
                className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-extrabold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                <i className="ri-vip-crown-line"></i> View Membership Plans
              </button>
            </div>
          </>
        )}
      </div>

      <Footer />

      {/* Membership Gate Modal */}
      {showMembershipGate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="bg-stone-900 px-6 py-5 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-amber-400 rounded-full mx-auto mb-3">
                <i className="ri-vip-crown-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-extrabold text-lg">Membership Required</h3>
              <p className="text-stone-400 text-xs mt-1">To send quotes to buyers</p>
            </div>
            <div className="px-6 py-5">
              <p className="text-stone-600 text-sm mb-4 text-center">
                To send a quote for <strong className="text-stone-900">"{lockedPostTitle.length > 50 ? lockedPostTitle.slice(0, 50) + "…" : lockedPostTitle}"</strong>, your factory needs an active membership.
              </p>
              <div className="space-y-2 mb-5">
                {["View full buyer contact details", "Send unlimited quotes to buyers", `Access all ${buyerPosts.length} active buyer requests`, "Get verified supplier badge", "Priority placement in search results"].map((f) => (
                  <div key={f} className="flex items-center gap-2 text-xs text-stone-600">
                    <i className="ri-checkbox-circle-fill text-amber-400 shrink-0"></i>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowMembershipGate(false)} className="flex-1 border border-stone-200 text-stone-600 font-semibold py-2.5 rounded-full text-sm cursor-pointer hover:bg-stone-50 transition-colors whitespace-nowrap">
                  Maybe Later
                </button>
                <button onClick={() => { setShowMembershipGate(false); navigate("/membership"); }} className="flex-1 bg-amber-400 hover:bg-amber-500 text-white font-bold py-2.5 rounded-full text-sm cursor-pointer transition-colors whitespace-nowrap">
                  View Plans
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
